import { ChevronDown, Plus, Check } from 'lucide-react'
import { RefObject } from 'react'

interface Curation {
  id: string
  title: string
  item_count?: number
}

interface CurationDropdownProps {
  dropdownRef: RefObject<HTMLDivElement | null>
  showDropdown: boolean
  setShowDropdown: (show: boolean) => void
  userCurations: Curation[]
  curationsWithItem: Set<string>
  showCreateCuration: boolean
  setShowCreateCuration: (show: boolean) => void
  newCurationTitle: string
  setNewCurationTitle: (title: string) => void
  isCreatingCuration: boolean
  onToggleCuration: (curationId: string) => void
  onCreateCuration: () => void
  // Variant support for different contexts
  variant?: 'default' | 'icon'
  // Theme for icon variant (light = white bg, dark = transparent bg for overlays)
  theme?: 'light' | 'dark'
  // "Saved Items" row at top of dropdown
  isSaved?: boolean
  onSaveToggle?: () => void
  // Custom trigger element (replaces built-in trigger button)
  customTrigger?: (toggleDropdown: () => void) => React.ReactNode
  // Dropdown position relative to trigger
  dropdownPosition?: 'below' | 'above'
}

export function CurationDropdown({
  dropdownRef,
  showDropdown,
  setShowDropdown,
  userCurations,
  curationsWithItem,
  showCreateCuration,
  setShowCreateCuration,
  newCurationTitle,
  setNewCurationTitle,
  isCreatingCuration,
  onToggleCuration,
  onCreateCuration,
  variant = 'default',
  theme = 'light',
  isSaved,
  onSaveToggle,
  customTrigger,
  dropdownPosition = 'below'
}: CurationDropdownProps) {
  const handleCancelCreate = () => {
    setShowCreateCuration(false)
    setNewCurationTitle('')
  }

  // Trigger button styles based on variant and theme
  const getTriggerButton = () => {
    if (customTrigger) {
      return customTrigger(() => setShowDropdown(!showDropdown))
    }

    if (variant === 'icon') {
      const buttonClass = theme === 'dark'
        ? 'w-10 h-10 rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-105 bg-white/10 text-white hover:bg-white/20'
        : 'w-10 h-10 rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-105 bg-white/90 backdrop-blur text-black hover:bg-white'

      return (
        <button
          onClick={(e) => {
            e.stopPropagation()
            setShowDropdown(!showDropdown)
          }}
          className={buttonClass}
          title="Add to curation"
          aria-expanded={showDropdown}
          aria-haspopup="listbox"
        >
          <Plus className={`h-5 w-5 transition-transform ${showDropdown ? 'rotate-45' : ''}`} />
        </button>
      )
    }

    // Default variant - text button with chevron
    return (
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="flex items-center gap-1 px-3 py-1.5 text-sm hover:bg-gray-100 rounded-lg transition-colors"
        style={{ fontFamily: "'GT America Mono', 'Courier New', monospace" }}
        aria-expanded={showDropdown}
        aria-haspopup="listbox"
      >
        Add to Curation
        <ChevronDown className={`h-4 w-4 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
      </button>
    )
  }

  // Dropdown panel styles based on variant and position
  const getDropdownPanelClass = () => {
    const positionClass = dropdownPosition === 'above'
      ? 'absolute right-0 bottom-full mb-2'
      : 'absolute right-0 top-full mt-2'

    if (variant === 'icon' || customTrigger) {
      return `${positionClass} w-56 bg-white rounded-2xl shadow-xl border border-gray-100 py-3 z-50 max-h-64 overflow-y-auto`
    }
    return `${positionClass} w-72 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50 max-h-96 overflow-y-auto`
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {getTriggerButton()}

      {showDropdown && (
        <div
          className={getDropdownPanelClass()}
          onClick={(e) => e.stopPropagation()}
          role="listbox"
          aria-label="Curations"
        >
          {/* "Saved" primary action row at top when save toggle is provided */}
          {onSaveToggle && (
            (variant === 'icon' || customTrigger) ? (
              <button
                onClick={() => onSaveToggle()}
                className={`w-full flex items-center gap-2 px-4 py-2.5 text-[13px] font-semibold hover:bg-gray-50 transition-colors ${
                  isSaved ? 'text-black' : 'text-gray-600'
                }`}
                role="option"
                aria-selected={isSaved}
              >
                {isSaved && <Check className="h-4 w-4 text-black flex-shrink-0" />}
                <span>{isSaved ? 'Saved' : 'Save to Saved Items'}</span>
              </button>
            ) : (
              <button
                onClick={() => onSaveToggle()}
                className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors flex items-center gap-3"
                style={{ fontFamily: "'GT America Trial Regular', -apple-system, sans-serif" }}
                role="option"
                aria-selected={isSaved}
              >
                <div className="flex items-center justify-center w-5 h-5">
                  <input
                    type="checkbox"
                    checked={isSaved || false}
                    onChange={() => {}}
                    className="w-4 h-4 rounded border-gray-300 text-black focus:ring-black cursor-pointer"
                  />
                </div>
                <div className="flex-1">
                  <div className="font-medium">Saved Items</div>
                </div>
              </button>
            )
          )}

          {/* Separator after Saved */}
          {onSaveToggle && (
            <div className={`border-t border-gray-${(variant === 'icon' || customTrigger) ? '100' : '200'} ${(variant === 'icon' || customTrigger) ? 'my-2' : 'my-1'}`}></div>
          )}

          {/* "ADD TO CURATION" section header */}
          {(variant === 'icon' || customTrigger) && onSaveToggle && (
            <span className="block text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-1 px-4">
              Add to Curation
            </span>
          )}

          {userCurations.length === 0 && !showCreateCuration && !onSaveToggle ? (
            <div className={`text-sm text-gray-500 ${variant === 'icon' ? 'px-4 py-2 text-xs text-gray-400' : 'px-4 py-3'}`}>
              No curations yet
            </div>
          ) : (
            userCurations.map((curation) => {
              const isInCuration = curationsWithItem.has(curation.id)

              if (variant === 'icon') {
                // Compact style for card overlay
                return (
                  <button
                    key={curation.id}
                    onClick={() => onToggleCuration(curation.id)}
                    className={`w-full flex items-center justify-between px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                      isInCuration ? 'text-[#57241F] font-medium bg-[#57241F]/5' : 'text-gray-700'
                    }`}
                    role="option"
                    aria-selected={isInCuration}
                  >
                    <span className="truncate">{curation.title}</span>
                    {isInCuration && <Check className="h-4 w-4 text-[#57241F] flex-shrink-0" />}
                  </button>
                )
              }

              // Default style with checkbox
              return (
                <button
                  key={curation.id}
                  onClick={() => onToggleCuration(curation.id)}
                  className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors flex items-center gap-3"
                  style={{ fontFamily: "'GT America Trial Regular', -apple-system, sans-serif" }}
                  role="option"
                  aria-selected={isInCuration}
                >
                  <div className="flex items-center justify-center w-5 h-5">
                    <input
                      type="checkbox"
                      checked={isInCuration}
                      onChange={() => {}}
                      className="w-4 h-4 rounded border-gray-300 text-black focus:ring-black cursor-pointer"
                    />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium">{curation.title}</div>
                    {curation.item_count !== undefined && (
                      <div className="text-xs text-gray-500 mt-0.5">
                        {curation.item_count} {curation.item_count === 1 ? 'item' : 'items'}
                      </div>
                    )}
                  </div>
                </button>
              )
            })
          )}

          {/* Separator */}
          {userCurations.length > 0 && (
            <div className={`border-t border-gray-${variant === 'icon' ? '100' : '200'} ${variant === 'icon' ? 'mt-2 pt-2' : 'my-2'}`}></div>
          )}

          {/* Create New Curation */}
          {!showCreateCuration ? (
            <button
              onClick={() => setShowCreateCuration(true)}
              className={`w-full text-left transition-colors ${
                variant === 'icon'
                  ? 'flex items-center gap-2 px-4 py-2 text-sm text-gray-400 hover:text-gray-600'
                  : 'px-4 py-2 text-sm hover:bg-gray-50 font-medium'
              }`}
              style={variant === 'default' ? { fontFamily: "'GT America Mono', 'Courier New', monospace" } : undefined}
            >
              {variant === 'icon' ? (
                <>
                  <Plus className="h-4 w-4" />
                  <span>Create New</span>
                </>
              ) : (
                '+ Create New Curation'
              )}
            </button>
          ) : (
            <div className={variant === 'icon' ? 'px-4 space-y-2' : 'px-4 py-3'}>
              <input
                type="text"
                value={newCurationTitle}
                onChange={(e) => setNewCurationTitle(e.target.value)}
                placeholder={variant === 'icon' ? 'Curation name...' : 'Curation title...'}
                className={`w-full text-sm border focus:outline-none ${
                  variant === 'icon'
                    ? 'px-3 py-1.5 border-gray-200 rounded-lg focus:ring-1 focus:ring-[#57241F]'
                    : 'px-3 py-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-black mb-2'
                }`}
                style={variant === 'default' ? { fontFamily: "'GT America Trial Regular', -apple-system, sans-serif" } : undefined}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    onCreateCuration()
                  } else if (e.key === 'Escape') {
                    handleCancelCreate()
                  }
                }}
              />
              <div className="flex gap-2">
                {variant === 'icon' ? (
                  <>
                    <button
                      onClick={handleCancelCreate}
                      className="flex-1 px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 rounded"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={onCreateCuration}
                      disabled={!newCurationTitle.trim() || isCreatingCuration}
                      className="flex-1 px-2 py-1 text-xs bg-[#57241F] text-white rounded hover:bg-[#3D1815] disabled:opacity-50"
                    >
                      {isCreatingCuration ? 'Creating...' : 'Create'}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={onCreateCuration}
                      disabled={!newCurationTitle.trim() || isCreatingCuration}
                      className="flex-1 px-3 py-1.5 text-xs bg-black text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ fontFamily: "'GT America Mono', 'Courier New', monospace" }}
                    >
                      {isCreatingCuration ? 'Creating...' : 'Create & Add'}
                    </button>
                    <button
                      onClick={handleCancelCreate}
                      className="px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded-lg"
                      style={{ fontFamily: "'GT America Mono', 'Courier New', monospace" }}
                    >
                      Cancel
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
